import React from 'react';
import {
  View,
  Text,
  Platform,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { useTheme } from '../context/themeContext';

interface EmptyProps {
  title?: string;
  children?: React.ReactNode;
  action?: React.ReactNode;
  icon?: React.ReactNode;
  style?: ViewStyle;
}

export default function Empty({ title, children, action, icon, style }: EmptyProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;

  const containerStyle: ViewStyle = {
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: colors.lineStrong,
    borderRadius: radii.r3,
    paddingVertical: 32,
    paddingHorizontal: 24,
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: { textAlign: 'center' } as ViewStyle,
      default: {},
    }),
  };

  const titleStyle: TextStyle = {
    fontSize: fontSize.md,
    fontWeight: fontWeight.medium,
    color: colors.ink2,
    fontFamily: theme.font.sans,
    marginBottom: 4,
    includeFontPadding: false,
    textAlign: 'center',
  };

  const childTextStyle: TextStyle = {
    fontSize: fontSize.md,
    color: colors.ink3,
    fontFamily: theme.font.sans,
    textAlign: 'center',
    includeFontPadding: false,
  };

  return (
    <View style={[containerStyle, style]}>
      {icon && (
        <View style={{ marginBottom: 10, alignItems: 'center', color: colors.ink4 } as ViewStyle}>
          {icon}
        </View>
      )}
      {title && <Text style={titleStyle}>{title}</Text>}
      {children && (
        // Text-like children (a string, number, or an array such as
        // `["Add a field to ", name, "…"]`) get the themed body color; only a
        // real element node (e.g. a custom action) renders as a bare View.
        React.isValidElement(children)
          ? <View style={{ alignItems: 'center' }}>{children}</View>
          : <Text style={childTextStyle}>{children}</Text>
      )}
      {action && (
        <View style={{ marginTop: 14, alignItems: 'center' }}>
          {action}
        </View>
      )}
    </View>
  );
}
